import { FiPlus, FiTrash2 } from "react-icons/fi";
import Input from "../common/Input";
import Button from "../common/Button";
import DateSelector from "./DateSelector";
import TimeSlotPicker from "./TimeSlotPicker";
import {
  AUTO_MOBILE_PARTY_SIZE_THRESHOLD,
  HENNA_TYPE_OPTIONS,
  HENNA_TYPES,
  JAGUAR_HENNA_DISCLAIMER,
  LOCATION_NOTES_DESCRIPTION,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
  STUDIO_MODE_DESCRIPTION,
} from "../../utils/constants";
import formatCurrency from "../../utils/formatCurrency";

const BookingItemCard = ({
  item,
  index,
  services,
  errors,
  derivedItem,
  canRemove,
  onItemChange,
  onRemoveService,
}) => {
  const isJaguarSelected = item?.hennaType === HENNA_TYPES.JAGUAR;

  return (
    <div className="rounded-3xl border border-[#ead8c0] bg-[#fffdfa] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-henna">
            Service {index + 1}
          </h3>
          <p className="mt-1 text-sm leading-6 text-muted-warm">
            Choose the service, henna type, and party breakdown for this line
            item.
          </p>
        </div>

        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemoveService(index)}
            leftIcon={<FiTrash2 size={16} />}
            className="self-start"
          >
            Remove
          </Button>
        ) : null}
      </div>

      <div className="mt-5 space-y-5">
        <div>
          <label
            className="field-label"
            htmlFor={`bookingItems.${index}.serviceId`}
          >
            Service <span className="text-red-500">*</span>
          </label>

          <select
            id={`bookingItems.${index}.serviceId`}
            name={`bookingItems.${index}.serviceId`}
            value={item?.serviceId || ""}
            onChange={(event) =>
              onItemChange(index, "serviceId", event.target.value)
            }
            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#2f2626] shadow-sm transition focus:outline-none focus:ring-2 ${
              errors?.serviceId
                ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                : "border-[#d7c2a3] focus:border-[#8B3A3A] focus:ring-[#ead8c0]"
            }`}
          >
            <option value="">Select a service</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {service.serviceName}
                {service.servicePrice !== undefined &&
                service.servicePrice !== null
                  ? ` - $${Number(service.servicePrice).toFixed(2)}`
                  : ""}
              </option>
            ))}
          </select>

          {errors?.serviceId ? (
            <p className="field-error">{errors.serviceId}</p>
          ) : derivedItem?.service?.serviceDescription ? (
            <p className="field-hint">{derivedItem.service.serviceDescription}</p>
          ) : null}
        </div>

        <div>
          <label
            className="field-label"
            htmlFor={`bookingItems.${index}.hennaType`}
          >
            Henna Type <span className="text-red-500">*</span>
          </label>

          <select
            id={`bookingItems.${index}.hennaType`}
            name={`bookingItems.${index}.hennaType`}
            value={item?.hennaType || ""}
            onChange={(event) =>
              onItemChange(index, "hennaType", event.target.value)
            }
            className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#2f2626] shadow-sm transition focus:outline-none focus:ring-2 ${
              errors?.hennaType
                ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                : "border-[#d7c2a3] focus:border-[#8B3A3A] focus:ring-[#ead8c0]"
            }`}
          >
            <option value="">Select a henna type</option>
            {HENNA_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
                {option === HENNA_TYPES.JAGUAR ? " (+$25)" : ""}
              </option>
            ))}
          </select>

          {errors?.hennaType ? (
            <p className="field-error">{errors.hennaType}</p>
          ) : (
            <p className="field-hint">
              Each selected service keeps its own henna type, duration, and
              price.
            </p>
          )}
        </div>

        {isJaguarSelected ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-henna">
              Jaguar Henna Notice
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {JAGUAR_HENNA_DISCLAIMER}
            </p>
          </div>
        ) : null}

        <div className="form-grid two">
          <Input
            label="Number of Adults"
            name={`bookingItems.${index}.numOfAdults`}
            type="number"
            min="0"
            value={item?.numOfAdults ?? 0}
            onChange={(event) =>
              onItemChange(index, "numOfAdults", event.target.value)
            }
            error={errors?.numOfAdults}
            required
          />

          <Input
            label="Number of Children"
            name={`bookingItems.${index}.numOfChildren`}
            type="number"
            min="0"
            value={item?.numOfChildren ?? 0}
            onChange={(event) =>
              onItemChange(index, "numOfChildren", event.target.value)
            }
            error={errors?.numOfChildren}
            required
          />
        </div>

        <div className="rounded-2xl bg-soft-cream p-4">
          <p className="text-sm font-semibold text-henna">
            Service Line Summary
          </p>
          <div className="mt-3 space-y-2 text-sm text-muted-warm">
            <p>
              Party size:{" "}
              <span className="font-semibold text-[#3b2f2f]">
                {derivedItem?.partySize || 0}
              </span>
            </p>
            <p>
              Estimated line total:{" "}
              <span className="font-semibold text-[#3b2f2f]">
                {derivedItem?.service
                  ? formatCurrency(derivedItem.bookingPrice || 0)
                  : "Select a service"}
              </span>
            </p>
            <p>
              Estimated line duration:{" "}
              <span className="font-semibold text-[#3b2f2f]">
                {derivedItem?.service
                  ? `${derivedItem.bookingDurationMinutes || 0} minutes`
                  : "Select a service"}
              </span>
            </p>
          </div>

          {errors?.partySize ? (
            <p className="field-error">{errors.partySize}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const BookingForm = ({
  formData,
  errors = {},
  services = [],
  bookingItemsWithDetails = [],
  totals = {
    totalPartySize: 0,
    totalPrice: 0,
    totalDurationMinutes: 0,
    serviceMode: SERVICE_MODE.STUDIO,
  },
  onChange,
  onItemChange,
  onAddService,
  onRemoveService,
  onSubmit,
  onSelectSlot,
  availableDates = [],
  availableSlots = [],
  slotsLoading = false,
  loading = false,
  isGuest = false,
  submitLabel,
}) => {
  const resolvedSubmitLabel =
    submitLabel || (isGuest ? "Continue as Guest" : "Confirm Appointment");

  const isMobileMode = formData?.serviceMode === SERVICE_MODE.MOBILE;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="content-card-lg p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-henna">
              Choose Your Services
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              Add one or more services to the same booking. Each service line
              keeps its own henna type, party count, price, and duration.
            </p>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onAddService}
            leftIcon={<FiPlus size={16} />}
            className="self-start"
          >
            Add Another Service
          </Button>
        </div>

        <div className="mt-6 space-y-5">
          {(formData?.bookingItems || []).map((item, index) => (
            <BookingItemCard
              key={index}
              item={item}
              index={index}
              services={services}
              errors={errors?.bookingItems?.[index] || {}}
              derivedItem={bookingItemsWithDetails[index]}
              canRemove={(formData?.bookingItems || []).length > 1}
              onItemChange={onItemChange}
              onRemoveService={onRemoveService}
            />
          ))}
        </div>
      </div>

      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Choose Date and Time</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Select a date from the calendar, then choose from the available time
          slots based on the full combined duration of your booking.
        </p>

        <div className="mt-6 grid gap-5 xl:grid-cols-[1.1fr_1.7fr]">
          <div className="min-w-0">
            <DateSelector
              value={formData?.bookingDate || ""}
              onChange={onChange}
              error={errors?.bookingDate}
              availableDates={availableDates}
            />
          </div>

          <div className="min-w-0">
            <TimeSlotPicker
              selectedDate={formData?.bookingDate}
              slots={availableSlots}
              selectedStartTime={formData?.startTime || ""}
              onSelectSlot={onSelectSlot}
              error={errors?.startTime || errors?.endTime}
              loading={slotsLoading}
            />
          </div>
        </div>

        {formData?.startTime && formData?.endTime ? (
          <div className="mt-6 rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">Selected Time</p>
            <p className="mt-2 text-sm text-muted-warm">
              {formData.startTime} - {formData.endTime}
            </p>
          </div>
        ) : null}
      </div>

      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Booking Overview</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Review the overall totals for all selected services combined.
        </p>

        <div className="mt-5 space-y-5">
          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">Combined Totals</p>
            <div className="mt-3 space-y-2 text-sm text-muted-warm">
              <p>
                Total people in this booking:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {totals.totalPartySize}
                </span>
              </p>
              <p>
                Estimated total price:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {formatCurrency(totals.totalPrice || 0)}
                </span>
              </p>
              <p>
                Estimated total duration:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {totals.totalDurationMinutes || 0} minutes
                </span>
              </p>
            </div>
            {errors?.partySize ? (
              <p className="field-error">{errors.partySize}</p>
            ) : null}
          </div>

          <Input
            label="Additional Notes"
            name="notes"
            value={formData?.notes || ""}
            onChange={onChange}
            error={errors?.notes}
            textarea
            rows={4}
            placeholder="Optional notes about your event, preferences, or anything you would like the artist to know."
          />
        </div>
      </div>

      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Appointment Type</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Your appointment type is chosen automatically based on the total party
          size across all selected services.
        </p>

        <div className="mt-5 space-y-4">
          <div className="rounded-2xl border border-[#d7c2a3] bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">
              How the Booking Type Is Chosen
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              If the total party size is less than{" "}
              {AUTO_MOBILE_PARTY_SIZE_THRESHOLD}, your appointment is
              automatically set to <span className="font-semibold">Studio</span>.
              If the total party size is{" "}
              {AUTO_MOBILE_PARTY_SIZE_THRESHOLD} or more, your appointment is
              automatically set to <span className="font-semibold">Mobile</span>.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-[#ead8c0] bg-white p-4">
                <p className="text-sm font-semibold text-henna">Studio</p>
                <p className="mt-2 text-sm leading-6 text-muted-warm">
                  {STUDIO_MODE_DESCRIPTION}
                </p>
              </div>

              <div className="rounded-2xl border border-[#ead8c0] bg-white p-4">
                <p className="text-sm font-semibold text-henna">Mobile</p>
                <p className="mt-2 text-sm leading-6 text-muted-warm">
                  {MOBILE_MODE_DESCRIPTION}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-[#d7c2a3] bg-white px-4 py-3 text-sm text-muted-warm">
              Current appointment type:{" "}
              <span className="font-semibold text-[#3b2f2f]">
                {formData?.serviceMode || "Not selected"}
              </span>
            </div>

            {isMobileMode ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-[#6b4b1f]">
                <span className="font-semibold">
                  Mobile appointment selected.
                </span>{" "}
                The artist will travel to your location, and the client is
                responsible for the artist&apos;s travel expenses.
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-[#d7c2a3] bg-soft-cream px-4 py-3 text-sm text-muted-warm">
                This booking is currently set to{" "}
                <span className="font-semibold text-[#3b2f2f]">Studio</span>.
              </div>
            )}
          </div>
        </div>

        {isMobileMode ? (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-henna">
                Mobile Appointment Details
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {LOCATION_NOTES_DESCRIPTION}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {MOBILE_TRAVEL_EXPENSES_DISCLAIMER}
              </p>
            </div>

            <div className="form-grid two">
              <Input
                label="Address Line 1"
                name="onsiteAddress.line1"
                value={formData?.onsiteAddress?.line1 || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.line1}
                required
                placeholder="Street address"
              />

              <Input
                label="Address Line 2"
                name="onsiteAddress.line2"
                value={formData?.onsiteAddress?.line2 || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.line2}
                placeholder="Apartment, suite, unit, or building"
              />
            </div>

            <div className="form-grid three">
              <Input
                label="City"
                name="onsiteAddress.city"
                value={formData?.onsiteAddress?.city || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.city}
                required
              />

              <Input
                label="State"
                name="onsiteAddress.state"
                value={formData?.onsiteAddress?.state || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.state}
                required
              />

              <Input
                label="ZIP Code"
                name="onsiteAddress.zip"
                value={formData?.onsiteAddress?.zip || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.zip}
                required
              />
            </div>

            <Input
              label="Location Notes"
              name="locationNotes"
              value={formData?.locationNotes || ""}
              onChange={onChange}
              error={errors?.locationNotes}
              textarea
              rows={4}
              placeholder="Gate code, apartment number, parking guidance, landmarks, or arrival instructions."
              hint={LOCATION_NOTES_DESCRIPTION}
            />
          </div>
        ) : null}
      </div>

      {isGuest ? (
        <div className="content-card-lg p-6">
          <h2 className="text-xl font-semibold text-henna">Guest Details</h2>
          <p className="mt-2 text-sm leading-6 text-muted-warm">
            Please provide the guest information needed to complete the
            appointment.
          </p>

          <div className="mt-5 space-y-5">
            <div className="form-grid two">
              <Input
                label="First Name"
                name="guest.firstName"
                value={formData?.guest?.firstName || ""}
                onChange={onChange}
                error={errors?.guestFirstName}
                required
              />

              <Input
                label="Last Name"
                name="guest.lastName"
                value={formData?.guest?.lastName || ""}
                onChange={onChange}
                error={errors?.guestLastName}
                required
              />
            </div>

            <div className="form-grid two">
              <Input
                label="Email"
                name="guest.email"
                type="email"
                value={formData?.guest?.email || ""}
                onChange={onChange}
                error={errors?.guestEmail}
                required
              />

              <Input
                label="Phone Number"
                name="guest.phoneNumber"
                value={formData?.guest?.phoneNumber || ""}
                onChange={onChange}
                error={errors?.guestPhoneNumber}
                required
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className="content-card-lg p-6">
        <div className="flex justify-center">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            disabled={!formData?.startTime}
            className="min-w-[240px]"
          >
            {resolvedSubmitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BookingForm;
